// Human labels for the permission taxonomy (spec/07 §6, F-IAM-11). The wire form is a `resource:action`
// slug (packages/contracts/src/permissions), which reads as a wall of code on the role screen. This turns
// each slug into a readable action and groups the actions under their resource, so a role reads as
// "Venues — View, Create, Edit" instead of "venues:archive, venues:create, venues:read, venues:update".
//
// English-only product (owner, 2026-09-23), so these are plain constants beside the staff screens that
// render them — a presentation taxonomy, not translated prose. Every resource and action in the contract's
// permission list has an entry here, and `__tests__/permission-labels.test.ts` fails the build when one is
// added without a name. The fallback (spaces for underscores, first letter capitalised) is only a safety
// net so a new slug never shows as `On_account` if it slips through.

/** The resource half of a slug (`venues:read` → `venues`), as a readable group name. */
export const RESOURCE_LABELS: Readonly<Record<string, string>> = {
  audit: 'Audit log',
  boot: 'Diskless boot',
  calls: 'Staff calls',
  credit: 'Pay-later credit',
  discounts: 'Discounts',
  floor: 'Floor',
  franchise: 'Franchise',
  hq: 'HQ',
  inventory: 'Stock',
  kitchen: 'Kitchen',
  library: 'Game library',
  members: 'Staff',
  orders: 'Orders',
  org: 'Organisation',
  passes: 'Passes',
  players: 'Players',
  pricing: 'Rate cards',
  products: 'Products',
  reports: 'Reports',
  sessions: 'Sessions',
  settings: 'Settings',
  shifts: 'Shifts',
  stations: 'Stations',
  tax: 'Tax',
  venues: 'Venues',
  wallet: 'Wallet',
};

/** The action half of a slug (`venues:read` → `read`), as a readable verb or, for `hq:*`/`settings:*`, a sub-area. */
export const ACTION_LABELS: Readonly<Record<string, string>> = {
  read: 'View',
  financial: 'Financial reports',
  manage: 'Manage',
  create: 'Create',
  update: 'Edit',
  remove: 'Remove',
  archive: 'Archive',
  invite: 'Invite',
  roles: 'Assign roles',
  refund: 'Refund',
  void: 'Void',
  adjust: 'Adjust',
  topup: 'Top up',
  grant: 'Grant',
  comp: 'Comp',
  comp_unlimited: 'Comp past the setting’s limits',
  start: 'Start',
  end: 'End',
  lock: 'Lock',
  power: 'Power',
  maintenance: 'Maintenance',
  maintenance_in_session: 'Maintenance during a session',
  recovery: 'Recovery code',
  message: 'Message',
  alerts_clear: 'Clear health warnings',
  ban: 'Ban',
  notes: 'Notes',
  discount: 'Discount',
  availability: 'Availability',
  arrange: 'Arrange',
  own: 'Own shift',
  all: 'Everything',
  move: 'Move',
  id_check: 'Check ID',
  read_contact: 'View contact details',
  read_all: 'View all',
  on_account: 'Charge to account',
  refund_approve: 'Approve refunds',
  offline_code: 'Offline codes',
  cash_movements: 'Cash in and out',
  day_close: 'Close the day',
  close_any: 'Close any shift',
  write_off: 'Write off',
  drawer_open: 'Open the cash drawer',
  // inventory:* (stock, 2026-10-07)
  receive: 'Receive deliveries',
  count: 'Count stock',
  waste: 'Record waste',
  reports: 'Stock costs and reports',
  supplies: 'Supplies only',
  // boot:* (Clavian Boot)
  view: 'View',
  edit_image: 'Edit images',
  wipe: 'Wipe write-back and shader caches',
  // org:*
  billing: 'Billing and subscription',
  // hq:* sub-areas
  catalog: 'Catalogue',
  organizations: 'Organisations',
  players: 'Players',
  players_read: 'View players',
  releases: 'Station releases',
  settings: 'Settings',
  subscriptions: 'Subscriptions',
  // settings:* sub-areas
  fnb: 'Food & drink',
  hardware: 'Printers and hardware',
  kiosk: 'Kiosk',
  locker: 'Lockers',
  notifications: 'Notifications',
  payments: 'Payments',
  sessions: 'Sessions',
};

/** `refund_approve` → "Refund approve": words, not a slug. Only reached by a slug not named above. */
function humanise(slug: string): string {
  const words = slug.replace(/[_-]+/g, ' ').trim();
  return words.length === 0 ? words : words.charAt(0).toUpperCase() + words.slice(1);
}

/** The readable name of a resource, e.g. `venues` → "Venues", `hq` → "HQ". */
export function resourceLabel(resource: string): string {
  return RESOURCE_LABELS[resource] ?? humanise(resource);
}

/** The readable name of an action, e.g. `read` → "View", `topup` → "Top up". */
export function actionLabel(action: string): string {
  return ACTION_LABELS[action] ?? humanise(action);
}

/** One resource and the readable actions a role holds on it. */
export interface PermissionGroup {
  readonly resource: string;
  readonly label: string;
  readonly actions: readonly { readonly slug: string; readonly label: string }[];
}

/**
 * Group a role's `resource:action` permissions by resource and label each action, resource-alphabetical
 * with the actions alphabetical inside each. A slug with no colon groups under itself as a single action.
 */
export function groupPermissions(permissions: readonly string[]): readonly PermissionGroup[] {
  const byResource = new Map<string, { slug: string; label: string }[]>();
  for (const permission of permissions) {
    const separator = permission.indexOf(':');
    const resource = separator === -1 ? permission : permission.slice(0, separator);
    const action = separator === -1 ? '' : permission.slice(separator + 1);
    const actions = byResource.get(resource) ?? [];
    actions.push({ slug: permission, label: action === '' ? resourceLabel(resource) : actionLabel(action) });
    byResource.set(resource, actions);
  }

  return [...byResource.entries()]
    .map(([resource, actions]) => ({
      resource,
      label: resourceLabel(resource),
      actions: [...actions].sort((a, b) => a.label.localeCompare(b.label)),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}
